import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Typography } from "@material-tailwind/react";
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { whatsappUrl } from '../../../utils/whatsapp';

const PaymentsPerCar = () => {
    const { token } = useAuth();
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [plateFilter, setPlateFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [sortBy, setSortBy] = useState('date'); // 'date' | 'plate'
    const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
    const [openDriverPlate, setOpenDriverPlate] = useState(null);

    useEffect(() => {
        const fetchCars = async () => {
            setLoading(true);
            try {
                const t = token || localStorage.getItem('authToken') || '';
                const res = await fetch(`${rootUrl}/cars?include_driver=1`, {
                    headers: { 'Authorization': `Bearer ${t}` }
                });
                const data = await res.json();
                // Expecting API to return an array of cars with optional last WAR fields
                setCars(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error fetching cars', err);
                setError('Unable to load cars');
            } finally {
                setLoading(false);
            }
        };

        fetchCars();
    }, [token]);

    const filtered = useMemo(() => {
        if (!Array.isArray(cars)) return [];

        const sd = startDate ? new Date(startDate) : null;
        const ed = endDate ? new Date(endDate) : null;
        const minAmt = minAmount ? Number(minAmount) : null;

        let out = cars.filter(c => {
            if (plateFilter && !String(c.plate_no || '').toLowerCase().includes(plateFilter.toLowerCase())) return false;

            const d = c.last_war_date ? new Date(c.last_war_date) : (c.last_trans_date ? new Date(c.last_trans_date) : null);
            if (sd && d && d < sd) return false;
            if (ed && d && d > ed) return false;

            if (minAmt !== null) {
                const amt = c.last_war_amount !== undefined && c.last_war_amount !== null && c.last_war_amount !== '' ? Number(c.last_war_amount) : (c.last_trans_amount ? Number(c.last_trans_amount) : 0);
                if (isNaN(amt) || amt < minAmt) return false;
            }

            return true;
        });

        out.sort((a, b) => {
            if (sortBy === 'plate') {
                const A = String(a.plate_no || '').toLowerCase();
                const B = String(b.plate_no || '').toLowerCase();
                if (A < B) return sortDir === 'asc' ? -1 : 1;
                if (A > B) return sortDir === 'asc' ? 1 : -1;
                return 0;
            }

            // sortBy date
            const da = a.last_war_date ? new Date(a.last_war_date) : (a.last_trans_date ? new Date(a.last_trans_date) : null);
            const db = b.last_war_date ? new Date(b.last_war_date) : (b.last_trans_date ? new Date(b.last_trans_date) : null);
            const ta = da ? da.getTime() : 0;
            const tb = db ? db.getTime() : 0;
            if (ta < tb) return sortDir === 'asc' ? -1 : 1;
            if (ta > tb) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return out;
    }, [cars, plateFilter, startDate, endDate, minAmount, sortBy, sortDir]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Payments Per Car</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">List of cars with last WAR/payment summary</p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <Card className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2 flex-wrap">
                            <input
                                type="text"
                                placeholder="Filter by plate"
                                value={plateFilter}
                                onChange={(e) => setPlateFilter(e.target.value)}
                                className="border px-3 py-2 rounded-md text-sm"
                            />

                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="border px-3 py-2 rounded-md text-sm"
                                aria-label="Start date"
                            />

                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="border px-3 py-2 rounded-md text-sm"
                                aria-label="End date"
                            />

                            <input
                                type="number"
                                placeholder="Min amount"
                                value={minAmount}
                                onChange={(e) => setMinAmount(e.target.value)}
                                className="border px-3 py-2 rounded-md text-sm w-28"
                            />

                            <button
                                onClick={() => { setPlateFilter(''); setStartDate(''); setEndDate(''); setMinAmount(''); }}
                                className="px-3 py-2 bg-gray-100 rounded-md text-sm"
                            >
                                Reset
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600 mr-1">Sort:</label>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border px-2 py-1 rounded-md text-sm">
                                <option value="date">Date</option>
                                <option value="plate">Plate</option>
                            </select>
                            <button
                                onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                                className="px-2 py-1 border rounded-md text-sm"
                                title="Toggle sort direction"
                            >
                                {sortDir === 'asc' ? 'Asc' : 'Desc'}
                            </button>
                        </div>
                    </div>

                    {loading && <Typography className="text-sm">Loading...</Typography>}
                    {error && <Typography className="text-sm text-red-500">{error}</Typography>}

                    {!loading && !error && (
                        <>
                          {/* Mobile cards — visible on xs/sm only */}
                          <div className="md:hidden space-y-3">
                            {filtered.map((c) => {
                              const phoneRaw = c.driver_phone || c.driver_mobile || c.driver_contact || (c.driver_details && (c.driver_details.phone || c.driver_details.mobile)) || c.driver || '';
                              const phoneDigits = phoneRaw ? String(phoneRaw).replace(/\D/g, '') : '';
                              const waLink = phoneDigits ? whatsappUrl(phoneDigits) : null;
                              const driverBal = c.driver_details?.bal !== undefined && c.driver_details?.bal !== null ? Number(c.driver_details.bal).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A';
                              const lastPaid = c.last_war_date ? new Date(c.last_war_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
                              const lastAmt = c.last_war_amount ? Number(c.last_war_amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A';
                              return (
                                <div key={c.plate_no} className="bg-white border border-gray-100 rounded-xl shadow-sm p-4">
                                  {/* Card header */}
                                  <div className="flex items-center justify-between mb-3">
                                    <Link to={`/cars/${c.plate_no}`} className="text-lg font-bold text-[#FF5733] hover:underline">
                                      {c.plate_no}
                                    </Link>
                                    <span className="text-sm text-gray-500 font-medium">{c.driver_name || (c.driver_details && c.driver_details.name) || 'No driver'}</span>
                                  </div>

                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm mb-3">
                                    <div>
                                      <p className="text-xs text-gray-400 uppercase tracking-wide">Phone</p>
                                      {waLink ? (
                                        <a href={waLink} target="_blank" rel="noopener noreferrer" className="text-[#FF5733] font-medium">{phoneRaw}</a>
                                      ) : (
                                        <p className="text-gray-700">{phoneRaw || 'N/A'}</p>
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-400 uppercase tracking-wide">Driver Bal</p>
                                      <p className="text-gray-700 font-semibold tabular-nums text-right">{driverBal}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-400 uppercase tracking-wide">Last Paid</p>
                                      <p className="text-gray-600">{lastPaid}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-400 uppercase tracking-wide">Amount</p>
                                      <p className="text-gray-700 font-semibold tabular-nums text-right">{lastAmt}</p>
                                    </div>
                                    {c.last_war_ref && (
                                      <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">Ref</p>
                                        <p className="text-gray-600 text-xs truncate">{c.last_war_ref}</p>
                                      </div>
                                    )}
                                    {c.last_war_input_by && (
                                      <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">By</p>
                                        <p className="text-gray-600 text-xs">{c.last_war_input_by}</p>
                                      </div>
                                    )}
                                  </div>

                                  {/* Driver detail expand */}
                                  <div className="pt-2 border-t border-gray-100">
                                    <button
                                      onClick={() => setOpenDriverPlate(prev => prev === c.plate_no ? null : c.plate_no)}
                                      className="text-xs text-[#FF5733] font-semibold"
                                    >
                                      {openDriverPlate === c.plate_no ? 'Hide driver details ▲' : 'View driver details ▼'}
                                    </button>
                                    {openDriverPlate === c.plate_no && (
                                      <div className="mt-2 space-y-1 text-sm">
                                        <div className="flex justify-between"><span className="text-gray-400">ID No</span><span>{c.driver_details?.id_no || c.driver_id || 'N/A'}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">License</span><span>{c.driver_details?.license_no || 'N/A'}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">License Expiry</span><span>{c.driver_details?.license_expiry ? new Date(c.driver_details.license_expiry).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Status</span><span>{c.driver_details?.status || 'N/A'}</span></div>
                                        {c.driver_details?.drive_link && (
                                          <div className="flex justify-between"><span className="text-gray-400">Drive Link</span><a href={c.driver_details.drive_link} target="_blank" rel="noreferrer" className="text-blue-600 text-xs">Open</a></div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Desktop table — visible on md+ only */}
                          <div className="hidden md:block overflow-x-auto">
                            <table className="w-full table-auto text-left">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="p-3">Plate</th>
                                        <th className="p-3">Driver</th>
                                        <th className="p-3">Phone</th>
                                        <th className="p-3 text-right">Driver Bal</th>
                                        <th className="p-3">Last Paid</th>
                                        <th className="p-3 text-right">Amount</th>
                                        <th className="p-3">Ref</th>
                                        <th className="p-3">By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((c) => {
                                        const phoneRaw = c.driver_phone || c.driver_mobile || c.driver_contact || (c.driver_details && (c.driver_details.phone || c.driver_details.mobile)) || c.driver || '';
                                        const phoneDigits = phoneRaw ? String(phoneRaw).replace(/\D/g, '') : '';
                                        const waLink = phoneDigits ? whatsappUrl(phoneDigits) : null;
                                        return (
                                            <tr key={c.plate_no} className="even:bg-gray-50/50 hover:bg-brand-misty/20">
                                                <td className="p-3">
                                                    <Link to={`/cars/${c.plate_no}`} className="text-brand-primary font-medium">
                                                        {c.plate_no}
                                                    </Link>
                                                </td>
                                                <td className="p-3 relative">
                                                    <button
                                                        onClick={() => setOpenDriverPlate(prev => prev === c.plate_no ? null : c.plate_no)}
                                                        className="text-brand-primary font-medium"
                                                        aria-expanded={openDriverPlate === c.plate_no}
                                                        aria-controls={`driver-tip-${c.plate_no}`}
                                                    >
                                                        {c.driver_name || (c.driver_details && c.driver_details.name) || 'N/A'}
                                                    </button>

                                                    {openDriverPlate === c.plate_no && (
                                                        <div id={`driver-tip-${c.plate_no}`} className="absolute left-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm z-50">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <strong className="text-sm">Driver Details</strong>
                                                                <button onClick={() => setOpenDriverPlate(null)} className="text-xs text-gray-500">Close</button>
                                                            </div>
                                                            <div className="space-y-1">
                                                                <div className="flex justify-between"><span className="text-gray-600">Name</span><span>{c.driver_details?.name || c.driver_name || 'N/A'}</span></div>
                                                                <div className="flex justify-between"><span className="text-gray-600">Phone</span><span>{(c.driver_details && (c.driver_details.phone || c.driver_details.mobile)) || c.driver_phone || c.driver_mobile || 'N/A'}</span></div>
                                                                <div className="flex justify-between"><span className="text-gray-600">ID No</span><span>{c.driver_details?.id_no || c.driver_id || 'N/A'}</span></div>
                                                                <div className="flex justify-between"><span className="text-gray-600">License</span><span>{c.driver_details?.license_no || 'N/A'}</span></div>
                                                                <div className="flex justify-between"><span className="text-gray-600">License Expiry</span><span>{c.driver_details?.license_expiry ? new Date(c.driver_details.license_expiry).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</span></div>
                                                                <div className="flex justify-between"><span className="text-gray-600">Status</span><span>{c.driver_details?.status || 'N/A'}</span></div>
                                                                <div className="flex justify-between"><span className="text-gray-600">Balance</span><span className="font-mono">{c.driver_details?.bal !== undefined ? Number(c.driver_details.bal).toLocaleString('en-KE', { minimumFractionDigits: 2 }) : 'N/A'}</span></div>
                                                                <div className="flex justify-between"><span className="text-gray-600">Drive Link</span><span className="truncate max-w-[200px]"><a href={c.driver_details?.drive_link} target="_blank" rel="noreferrer" className="text-blue-600">Open</a></span></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3">
                                                    {waLink ? (
                                                        <a href={waLink} target="_blank" rel="noopener noreferrer" className="text-brand-primary">
                                                            {phoneRaw}
                                                        </a>
                                                    ) : (
                                                        (phoneRaw || 'N/A')
                                                    )}
                                                </td>
                                                <td className="p-3 text-right font-mono">{c.driver_details?.bal !== undefined && c.driver_details?.bal !== null ? Number(c.driver_details.bal).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</td>
                                                <td className="p-3">{c.last_war_date ? new Date(c.last_war_date).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</td>
                                                <td className="p-3 text-right font-mono">{c.last_war_amount ? Number(c.last_war_amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</td>
                                                <td className="p-3">{c.last_war_ref || 'N/A'}</td>
                                                <td className="p-3">{c.last_war_input_by || 'N/A'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                          </div>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default PaymentsPerCar;
